import {
    createContext,
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Updater, useImmer } from 'use-immer';
import {
    ClientUnassignedId,
    Consent,
    Credentials,
    InviteInfo,
    Organization,
    SignUpDetails,
    SSOCredentials,
    TClientUnassignedId,
    TherapySession,
    TherapySessionsPagedResponse,
    User,
    UserRole,
    VerificationCredentials,
} from '../types';
import { parsePlanId } from '@upheal/billing/utils/plans';
import { isActiveTrial } from '@upheal/billing/utils/trials';
import { setSnowplowUser } from '../utils/analytics/snowplow';
import api, { appServer } from '../utils/api';
import { browserStorage } from '../utils/cache/browserStorage';
import intercom from '../utils/intercom';
import { PagedResult } from '../utils/paged-results';
import {
    getPagedTherapySessions,
    getTherapySessionProgress,
    getTherapySessionRequest,
    TherapySessionProgressResponse,
} from '../utils/requests';
import { captureException, setSentryUser } from '../utils/sentry';
import {
    fireLogoutEvent,
    isTherapySessionInProgress,
} from '../utils/syncBrowserTabs';
import { clearLastActiveTime } from '../utils/useTerminateInactiveUserSession.utils';
import { hasTherapistPermissions } from '../utils/userUtils';
import { mergeSessions } from './sessionListUtils';
import logger from '@upheal/utils/logger';
import { ILogtailLog } from '@logtail/types';
import { verificationErrorToReason } from '../utils/verificationUtils';
import { ErrorReason } from '../utils/errorMessages';
import { useContractLazy } from './useContractLazy';
import { useContractsListLazy } from './useContractsListLazy';
import { ssoErrorToReason } from '../utils/ssoUtils';
import delay from 'delay';
import produce from 'immer';
import { CLIENT_GENERIC_UNASSIGNED_URL_PART } from '../utils/configuration';
import { useMount } from 'react-use';
import { featureFlagsNewlyEnabled } from '@upheal/utils/feature-flags';
import { trackUiEvent } from '../utils/analytics/ui-tracking';
import posthog from 'posthog-js';
import { waitForPostHogFlags } from '../hooks/useWaitForPostHogFlags';
import { getUserProperties } from '../libs/vendor/posthog/utils';
import { apolloClient } from '../utils/apollo';
import { GetCustomerDetailsDocument } from '@upheal/graphql/definitions/billing/GetCustomerDetails.generated';

export type DashboardSessions = {
    items: {
        sessionId: string;
        contractId: string | TClientUnassignedId;
        date: string;
    }[];
    totalCount: number;
};

export interface StateContextType {
    isAuthed: boolean;
    authTimestamp: number;
    user: User | null;
    updateUser: Updater<User | null>;

    missingConsents: Array<Consent> | null;
    setMissingConsents: Dispatch<SetStateAction<Array<Consent> | null>>;
    inviteInfo: InviteInfo | null;
    setInviteInfo: (inviteInfo: InviteInfo | null) => void;
    validateInvite: (
        inviteId: string,
    ) => Promise<{ inviteInfo: InviteInfo; consents: Consent[] }>;
    validateEmailCode: (code: string) => Promise<{
        inviteInfo: InviteInfo;
        consents: Consent[];
    }>;
    refetchMe: () => Promise<User>;
    getMe: () => Promise<User>;
    signIn: (creds: Credentials) => Promise<
        | {
              success: false;
              reason: ErrorReason;
          }
        | { success: true; user: User }
        | {
              success: false;
              reason: 'verification-required';
              verificationId: string;
              redactedPhone?: string | null;
          }
    >;
    signInVerify: (creds: VerificationCredentials) => Promise<
        | {
              success: false;
              reason: ErrorReason;
          }
        | { success: true; user: User }
    >;
    signInWithOtp: (otp: string) => Promise<User>;
    signInWithSSO: (
        creds: SSOCredentials,
        enableForDomain?: boolean,
    ) => Promise<
        | {
              success: false;
              reason: ErrorReason;
          }
        | { success: true; user: User }
    >;
    signInClient: (clientId: string) => Promise<{
        user: User;
        missingConsents: Consent[] | null;
        ownerUserId: string;
        organization: Organization | null;
    }>;
    signUp: (details: SignUpDetails) => Promise<User>;
    logout: (params?: { suppressWarning?: boolean }) => Promise<void>;

    organizations: Organization[];
    updateOrganizations: Updater<Organization[]>;

    dashboardSessions: DashboardSessions;
    refreshDashboardSessions: (
        uninvoiced?: boolean,
        includeInvoiceStatus?: boolean,
    ) => Promise<void>;
    loadMoreDashboardSessions: (
        uninvoiced?: boolean,
        includeInvoiceStatus?: boolean,
    ) => Promise<void>;
    // TODO(couples) rename getClientSessions + validate it works for couples
    getClientSessions: (clientId: string | undefined) => TherapySession[];
    // TODO(couples) rename getClientSessionsTotalCount + validate it works for couples
    getClientSessionsTotalCount: (
        clientId: string | undefined,
    ) => number | null;
    // TODO(couples) rename refreshClientSessions + validate it works for couples
    refreshClientSessions: (
        clientId: string,
        pageSize?: number,
        includeInvoiceStatus?: boolean,
    ) => Promise<TherapySessionsPagedResponse>;
    // TODO(couples) rename loadMoreClientSessions + validate it works for couples
    loadMoreClientSessions: (
        clientId: string,
        includeInvoiceStatus?: boolean,
    ) => Promise<TherapySessionsPagedResponse>;
    refreshSession: (sessionId: string) => Promise<TherapySession>;
    refreshSessionProgress: (
        contractId: string | undefined,
        sessionId: string,
    ) => Promise<void>;
    updateClientSession: (
        clientId: string | undefined,
        sessionId: TherapySession['sessionId'],
        sessionUpdater:
            | TherapySession
            | ((oldState?: TherapySession) => TherapySession | undefined),
    ) => void;
    deleteClientSession: (
        clientId: string | undefined,
        session: TherapySession,
    ) => void;
}

export const StateContext = createContext<StateContextType>(null!);

export default function AppStateProvider(props: PropsWithChildren<object>) {
    const [isAuthed, setIsAuthed] = useState(false);
    const [authTimestamp, setAuthTimestamp] = useState<number>(Infinity);
    const [user, updateUser] = useImmer<User | null>(null);
    const [missingConsents, setMissingConsents] =
        useState<Array<Consent> | null>(null);
    const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);

    const [sessionsDataPerClient, updateSessionsDataPerClient] = useImmer<
        Record<User['id'], PagedResult<TherapySession>>
    >({});
    const [dashboardSessions, updateDashboardSessions] =
        useImmer<DashboardSessions>({ items: [], totalCount: 0 });
    const [organizations, updateOrganizations] = useImmer<Organization[]>([]);

    const { pathname, search: searchQuery } = useLocation();

    const { fetchContract } = useContractLazy({ fetchPolicy: 'network-only' });
    const { fetchContracts } = useContractsListLazy({
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        const enrichLogs = async (log: ILogtailLog) => {
            return {
                ...log,
                userId: user?.id,
            };
        };

        logger.use(enrichLogs);

        return () => {
            logger.remove(enrichLogs);
        };
    }, [user?.id]);

    useMount(() => {
        browserStorage.resetSessionRecordingsDatabaseErrorsCount();
    });

    const fetchDashboardSessions = useCallback(
        async (
            offset: number,
            pageSize = 10,
            uninvoiced?: boolean,
            includeInvoiceStatus?: boolean,
        ) => {
            try {
                const response = await appServer.request(
                    getPagedTherapySessions({
                        offset,
                        pageSize,
                        uninvoiced,
                        includeInvoiceStatus,
                    }),
                );
                const sessions = response.data.items as TherapySession[];

                // We want to normalize the state to avoid stale data, source of truth is `clientSessions`
                const items = sessions
                    .map((session) => {
                        const { contractId } = session;

                        return {
                            sessionId: session.sessionId,
                            contractId: contractId ?? ClientUnassignedId,
                            date: session.date,
                        };
                    })
                    .filter(Boolean);

                updateDashboardSessions((draft) => {
                    return mergeSessions(draft, {
                        items,
                        totalCount: response.data.totalCount,
                        offset: response.data.offset,
                        pageSize: response.data.pageSize,
                    });
                });

                updateSessionsDataPerClient((draft) => {
                    const groupedByClient = sessions.reduce((acc, session) => {
                        const clientId =
                            session.contractId ?? ClientUnassignedId;

                        if (!acc[clientId]) {
                            acc[clientId] = [];
                        }
                        acc[clientId].push(session);
                        return acc;
                    }, {} as { [groupingId: string]: TherapySession[] });

                    Object.entries(groupedByClient).forEach(
                        ([clientId, sessions]) => {
                            draft[clientId] = mergeSessions(draft[clientId], {
                                items: sessions,
                                totalCount: null,
                            });
                        },
                    );
                });
            } catch (error) {
                logger.warn('Failed to fetch dashboard sessions.', {
                    offset,
                    pageSize,
                    error,
                });

                // Let the UI components handle the error
                return Promise.reject(error);
            }
        },
        [updateSessionsDataPerClient, updateDashboardSessions],
    );

    const refreshDashboardSessions = useCallback(
        (uninvoiced?: boolean, includeInvoiceStatus?: boolean) => {
            return fetchDashboardSessions(
                0,
                10,
                uninvoiced,
                includeInvoiceStatus,
            );
        },
        [fetchDashboardSessions],
    );

    const loadMoreDashboardSessions = useCallback(
        (uninvoiced?: boolean, includeInvoiceStatus?: boolean) => {
            const offset = dashboardSessions.items.length;
            return fetchDashboardSessions(
                offset,
                10,
                uninvoiced,
                includeInvoiceStatus,
            );
        },
        [dashboardSessions, fetchDashboardSessions],
    );

    const fetchClientSessions = useCallback(
        async (
            clientId: string,
            offset: number,
            pageSize = 10,
            includeInvoiceStatus?: boolean,
        ) => {
            try {
                const response = await appServer.request(
                    getPagedTherapySessions({
                        contractId: clientId, // TODO change for couples therapy support
                        offset,
                        pageSize,
                        includeInvoiceStatus,
                    }),
                );
                const refreshedSessionsData =
                    response.data as TherapySessionsPagedResponse;
                updateSessionsDataPerClient((draft) => {
                    const mergedSessionsData = mergeSessions(
                        draft[clientId],
                        refreshedSessionsData,
                    );
                    draft[clientId] = mergedSessionsData;
                });

                return refreshedSessionsData;
            } catch (error) {
                logger.warn('Failed to fetch client sessions', {
                    clientId,
                    offset,
                    pageSize,
                    error,
                });

                // Let the UI components handle the error
                return Promise.reject(error);
            }
        },
        [updateSessionsDataPerClient],
    );

    const getClientSessions = useCallback(
        (clientId: string | undefined) => {
            return (
                sessionsDataPerClient[clientId ?? ClientUnassignedId]?.items ||
                []
            );
        },
        [sessionsDataPerClient],
    );

    const getClientSessionsTotalCount = useCallback(
        (clientId: string | undefined) => {
            return (
                sessionsDataPerClient[clientId ?? ClientUnassignedId]
                    ?.totalCount ?? null
            );
        },
        [sessionsDataPerClient],
    );

    const refreshClientSessions = useCallback(
        (clientId: string, pageSize = 10, includeInvoiceStatus?: boolean) =>
            fetchClientSessions(clientId, 0, pageSize, includeInvoiceStatus),
        [fetchClientSessions],
    );

    const loadMoreClientSessions = useCallback(
        (clientId: string, includeInvoiceStatus?: boolean) => {
            const clientSessions = getClientSessions(clientId);

            return fetchClientSessions(
                clientId,
                clientSessions.length,
                10,
                includeInvoiceStatus,
            );
        },
        [getClientSessions, fetchClientSessions],
    );

    // This method supports both optimistic update and optimistic creation
    const updateClientSession: StateContextType['updateClientSession'] =
        useCallback(
            (clientId, sessionId, sessionUpdater) => {
                const oldSessions = getClientSessions(clientId);
                const oldSession = oldSessions.find(
                    (s) => s.sessionId === sessionId,
                );

                const updatedSession =
                    typeof sessionUpdater === 'function'
                        ? produce(oldSession, sessionUpdater)
                        : sessionUpdater;

                if (!updatedSession || oldSession === updatedSession) {
                    return;
                }

                const clientIdOrUnassigned = clientId ?? ClientUnassignedId;

                updateSessionsDataPerClient((draft) => {
                    draft[clientIdOrUnassigned] = mergeSessions(
                        draft[clientIdOrUnassigned],
                        {
                            items: [updatedSession],
                            totalCount: null,
                        },
                    );
                });

                updateDashboardSessions((draft) => {
                    if (!draft.items.find((s) => s.sessionId === sessionId)) {
                        draft.items.unshift({
                            sessionId: updatedSession.sessionId,
                            contractId: clientIdOrUnassigned,
                            date: updatedSession.date,
                        });
                        draft.totalCount++;
                    }
                });
            },
            [
                getClientSessions,
                updateDashboardSessions,
                updateSessionsDataPerClient,
            ],
        );

    const fetchSession = useCallback(
        async (sessionId: string) => {
            try {
                const response = await appServer.request(
                    getTherapySessionRequest(sessionId),
                );

                const refreshedSessionData = response.data as TherapySession;

                const clientId = refreshedSessionData.contractId;
                updateClientSession(clientId, sessionId, refreshedSessionData);

                return refreshedSessionData;
            } catch (error) {
                logger.warn('Failed to fetch session.', {
                    sessionId,
                    error,
                });

                return Promise.reject(error);
            }
        },
        [updateClientSession],
    );

    const refreshSession = useCallback(
        (sessionId: string) => fetchSession(sessionId),
        [fetchSession],
    );

    const refreshSessionProgress = useCallback(
        async (contractId: string | undefined, sessionId: string) => {
            try {
                const { data: responseData } =
                    await appServer.request<TherapySessionProgressResponse>(
                        getTherapySessionProgress(sessionId),
                    );

                const existingSession = getClientSessions(contractId)?.find(
                    (s) => s.sessionId === sessionId,
                );
                // upon change in progressNoteReady or state, do full refresh, otherwise the session would miss other important data we count on in eg. "Done" state.
                if (
                    (!existingSession?.progress?.detail.progressNoteReady &&
                        responseData.detail.progressNoteReady) ||
                    existingSession?.state !== responseData.state
                ) {
                    refreshSession(sessionId).catch(() => {
                        // swallow error
                    });
                    return;
                }

                updateClientSession(contractId, sessionId, (draft) => {
                    if (!draft) {
                        return;
                    }

                    draft.state = responseData.state;
                    if (draft.progress?.detail) {
                        draft.progress.detail.progressNoteReady =
                            responseData.detail.progressNoteReady;
                    }
                    draft.lastProcessingDateUtc =
                        responseData.lastProcessingDateUtc;
                    return draft;
                });
            } catch (error) {
                logger.warn('Failed to fetch session progress.', {
                    sessionId,
                    error,
                });
            }
        },
        [getClientSessions, refreshSession, updateClientSession],
    );

    const deleteClientSession = (
        clientId: string | undefined,
        session: TherapySession,
    ) => {
        updateSessionsDataPerClient((draft) => {
            const clientSessionsData = draft[clientId ?? ClientUnassignedId];
            if (!clientSessionsData) {
                return draft;
            }

            const sessionIndex = clientSessionsData.items.findIndex(
                (s) => s.sessionId === session.sessionId,
            );
            if (sessionIndex !== -1) {
                clientSessionsData.items.splice(sessionIndex, 1);
                if (clientSessionsData.totalCount) {
                    clientSessionsData.totalCount--;
                }
            }
        });
    };

    const loadInitialContracts = useCallback(async () => {
        const urlParts = pathname.split('/');
        const searchParams = new URLSearchParams(searchQuery);

        // TODO this is a performance problem workaround for the
        //  video call / audio recorder page - loading a single client instead of all clients.
        const contractId =
            (urlParts.length === 3 && urlParts[1] === 'video' && urlParts[2]) ||
            (urlParts.length === 2 &&
                urlParts[1] === 'recorder' &&
                searchParams.get('contractId'));
        if (contractId) {
            const contractId = urlParts[2];

            if (contractId.startsWith(CLIENT_GENERIC_UNASSIGNED_URL_PART)) {
                return;
            }

            const { data, error } = await fetchContract({
                variables: {
                    // contract with single client share the same id
                    contractId,
                },
            });

            if (!data?.contract || error) {
                captureException('Failed to fetch a client for a video call.', {
                    pathname,
                    error,
                });

                throw new Error('Failed to fetch a contract');
            }
        } else {
            const { data, error } = await fetchContracts();

            if (!data?.contracts || error) {
                logger.warn('Failed to fetch contracts list.', {
                    pathname,
                    error,
                });

                throw new Error('Failed to fetch contracts');
            }
        }
    }, [fetchContract, fetchContracts, pathname, searchQuery]);

    const refetchMe = useCallback(async () => {
        const responseMe = await api.getMe();
        const meUser: User = responseMe.data;
        updateUser(meUser);
        updateOrganizations(meUser.organizations ?? []);

        return meUser;
    }, [updateOrganizations, updateUser]);

    const loadPostHogFlags = useCallback(async () => {
        try {
            await waitForPostHogFlags();
        } catch (error) {
            // Don't block the app if feature flags fail to load
            captureException(
                '[posthog] failed to load for PostHog feature flags',
                {
                    error,
                },
            );
        }
    }, []);

    const getMe = async () => {
        try {
            const meUser = await refetchMe();
            setSentryUser(meUser);
            setSnowplowUser(meUser);
            featureFlagsNewlyEnabled.forEach((name) => {
                trackUiEvent('System', 'Feature Flag Enabled', undefined, name);
            });
            if (hasTherapistPermissions({ user: meUser })) {
                intercom.prepareInit(meUser);
            }

            if (![UserRole.Admin, UserRole.Client].includes(meUser.role)) {
                // Make this blocking, because UI still relies on this data to be already present when the app is rendered after load.
                // ...but in case of an error, or request taking too long, we don't want to block the whole app (suboptimal/unexpected UX is possible though, we would need to go through all occurrences of useContractsList and do proper loading/error handling)
                await Promise.race([
                    loadInitialContracts().catch(() => {}),
                    delay(20_000),
                ]);
            }
            setIsAuthed(true);
            setAuthTimestamp(Date.now());

            const subscription = await apolloClient
                .query({ query: GetCustomerDetailsDocument })
                .then((result) => result.data?.getCustomerDetails?.subscription)
                .catch((error) => {
                    captureException(
                        '[getMe] Failed to load customer subscription details',
                        error,
                    );
                    return undefined;
                });

            posthog.identify(
                meUser.id,
                getUserProperties(
                    meUser,
                    meUser.organizations?.[0],
                    subscription,
                ),
            );

            if (subscription) {
                posthog.register({
                    planTier: parsePlanId(subscription.planId).pricingTier,

                    isTrial: isActiveTrial(
                        subscription.customTrial ?? subscription.trial,
                    ),
                });
            }

            const orgId = meUser?.organizations?.[0]?.id;
            if (orgId) {
                posthog.group('organization', orgId);
            }

            // Wait for PostHog feature flags to load after identify
            await loadPostHogFlags();

            return meUser;
        } catch (error) {
            updateUser(null);
            setIsAuthed(false);
            throw error;
        }
    };

    const signIn: StateContextType['signIn'] = async (creds: Credentials) => {
        try {
            const response = await api.signIn(creds);
            if (response.verificationRequired && response.verificationId) {
                return {
                    success: false,
                    reason: 'verification-required',
                    verificationId: response.verificationId,
                    redactedPhone: response.redactedPhone,
                };
            }
        } catch (error: any) {
            return {
                success: false,
                reason:
                    error.response?.status === 401
                        ? 'invalid-credentials'
                        : verificationErrorToReason(error),
            };
        }

        try {
            const user = await getMe();
            return {
                success: true,
                user,
            };
        } catch {
            return {
                success: false,
                reason: 'network-error',
            };
        }
    };

    const signInVerify: StateContextType['signInVerify'] = async (
        creds: VerificationCredentials,
    ) => {
        try {
            await api.signInVerify(creds);
        } catch (error: any) {
            return {
                success: false,
                reason:
                    error.response?.status === 401 ? 'invalid-code' : 'unknown',
            };
        }

        try {
            const user = await getMe();
            return {
                success: true,
                user,
            };
        } catch {
            return {
                success: false,
                reason: 'network-error',
            };
        }
    };

    const signInWithOtp = (otp: string) => {
        return api.signInWithOtp(otp).then(getMe);
    };

    const signInWithSSO: StateContextType['signInWithSSO'] = async (
        creds: SSOCredentials,
        enableForDomain: boolean = false,
    ) => {
        try {
            await api.singInWithSSO(creds, enableForDomain);
        } catch (error: any) {
            return {
                success: false,
                reason: ssoErrorToReason(error),
            };
        }

        try {
            const user = await getMe();
            return {
                success: true,
                user,
            };
        } catch {
            return {
                success: false,
                reason: 'network-error',
            };
        }
    };

    const signInClient = async (inviteId: string) => {
        return api
            .signInClient(inviteId)
            .then(async ({ missingConsents, ownerUserId, organization }) => {
                setMissingConsents(missingConsents);
                const user = await getMe();

                return {
                    user,
                    missingConsents,
                    ownerUserId,
                    organization,
                };
            });
    };

    const validateInvite = async (inviteId: string) => {
        const newInviteInfo = await api.validateInvite(inviteId);
        setInviteInfo(newInviteInfo);
        const newConsents = await api.getInviteConsents(inviteId);
        setMissingConsents(newConsents);
        return { inviteInfo: newInviteInfo, consents: newConsents };
    };

    const validateEmailCode = async (code: string) => {
        const inviteInfo = (await api.validateEmailCode(code)) as InviteInfo & {
            inviteId: string;
        };
        setInviteInfo(inviteInfo);
        const consents = await api.getInviteConsents(inviteInfo.inviteId);
        setMissingConsents(consents);
        return { inviteInfo, consents };
    };

    const signUp = async (details: SignUpDetails) => {
        return api.signUp(details).then(getMe);
    };

    const logout = useCallback(
        async ({ suppressWarning }: { suppressWarning?: boolean } = {}) => {
            if (hasTherapistPermissions({ user })) {
                intercom.shutdown();
            }

            if (isTherapySessionInProgress()) {
                if (!suppressWarning) {
                    Swal.fire({
                        titleText: 'Session in progress!',
                        text: 'There is an active session in one of the browser windows. Please end the session before logging out.',
                        icon: 'warning',
                    });
                }

                return Promise.reject();
            }

            await api.logout();
            browserStorage.clearAuthToken();
            clearLastActiveTime();
            fireLogoutEvent();
            posthog.reset();
        },
        [user],
    );

    return (
        <StateContext.Provider
            value={{
                isAuthed,
                authTimestamp,
                user,
                updateUser,
                missingConsents,
                setMissingConsents,
                inviteInfo,
                setInviteInfo,
                validateInvite,
                validateEmailCode,
                refetchMe,
                getMe,
                signIn,
                signInVerify,
                signInWithOtp,
                signInClient,
                signInWithSSO,
                signUp,
                logout,
                organizations,
                updateOrganizations,
                dashboardSessions,
                refreshDashboardSessions,
                loadMoreDashboardSessions,
                getClientSessions,
                getClientSessionsTotalCount,
                refreshClientSessions,
                loadMoreClientSessions,
                refreshSession,
                refreshSessionProgress,
                updateClientSession,
                deleteClientSession,
            }}
        >
            {props.children}
        </StateContext.Provider>
    );
}

export function useAppState() {
    const context = useContext(StateContext);
    if (!context) {
        throw new Error('useAppState must be used within the AppStateProvider');
    }

    // @ts-ignore
    window._appState = context;

    return context;
}
